import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, BookOpen, Calendar } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const ITEMS_PER_PAGE = 12;

const ArticlesPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const res = await axios.get(`${API}/articles?limit=50&skip=0`);
      setArticles(res.data);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(articles.length / ITEMS_PER_PAGE));
  const visibleArticles = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return articles.slice(start, start + ITEMS_PER_PAGE);
  }, [articles, currentPage]);

  const changePage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <main className="min-h-screen bg-background py-24">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-primary hover:text-primary-700 font-medium mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Kthehu
        </Link>

        <div className="flex items-center gap-3 mb-10">
          <BookOpen className="w-8 h-8 text-accent" />
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-primary">
            Të gjithë artikujt
          </h1>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground">Duke ngarkuar artikujt...</p>
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-16 bg-card rounded-2xl">
            <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Nuk ka artikuj për momentin</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visibleArticles.map((article, index) => (
                <motion.div
                  key={article.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: index * 0.04 }}
                  className="w-full overflow-hidden bg-card rounded-2xl shadow-md p-4 sm:p-6 hover:shadow-xl transition-shadow flex flex-col h-full"
                >
                  <h2 className="font-heading font-semibold text-xl mb-3 text-foreground line-clamp-2 overflow-hidden break-words">
                    {article.title}
                  </h2>

                  <p
                    className="text-muted-foreground leading-relaxed mb-6 line-clamp-3 overflow-hidden break-words"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />

                  <div className="mt-auto flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(article.created_at).toLocaleDateString('sq-AL')}</span>
                    </div>

                    <Link
                      to={`/shkrime/${article.slug}`}
                      className="flex items-center gap-1 text-sm font-semibold text-accent hover:text-accent-700 transition-colors"
                    >
                      Lexo më tepër
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="flex items-center justify-center gap-3 mt-10">
              <button
                type="button"
                onClick={() => changePage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-5 py-2.5 rounded-lg border border-primary text-primary font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary hover:text-white transition-colors"
              >
                Previous
              </button>
              <span className="text-sm text-muted-foreground">
                {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => changePage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-5 py-2.5 rounded-lg border border-primary text-primary font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary hover:text-white transition-colors"
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </main>
  );
};

export default ArticlesPage;
